import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data";

export const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    onNavigate(`#${id}`);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className="fixed top-4 left-0 right-0 z-50 px-4"
        data-testid="main-navbar"
      >
        <nav
          className={`mx-auto max-w-6xl flex items-center justify-between rounded-full border-2 px-5 py-3 transition-all duration-300 ${
            scrolled
              ? "bg-white/90 backdrop-blur-xl border-slate-900/10 shadow-[0_10px_40px_rgba(15,23,42,0.10)]"
              : "bg-white/70 backdrop-blur-md border-white/60"
          }`}
        >
          <button
            onClick={() => onNavigate(0)}
            data-testid="nav-logo"
            className="flex items-center gap-2 font-display font-extrabold text-lg tracking-tight text-slate-900"
          >
            <img
              src="/images/logo.png"
              alt="Action Packed Adventures logo"
              className="h-10 w-10 rounded-full object-cover shadow-[0_6px_16px_rgba(56,189,248,0.5)]"
            />
            <span className="hidden sm:inline">
              Action Packed <span className="text-sky-500">Adventures</span>
            </span>
            <span className="sm:hidden">APA</span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="rounded-full px-4 py-2 font-semibold text-slate-700 hover:text-sky-600 hover:bg-sky-50 transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => go("booking")}
            data-testid="nav-book-now"
            className="hidden md:inline-flex items-center rounded-full bg-slate-900 px-6 py-2.5 font-display font-bold text-white shadow-[0_8px_24px_rgba(15,23,42,0.25)] hover:bg-sky-500 hover:shadow-[0_8px_28px_rgba(56,189,248,0.5)] transition-colors"
          >
            Book Now
          </motion.button>

          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="md:hidden grid h-10 w-10 place-items-center rounded-full bg-slate-900 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 left-4 right-4 z-40 md:hidden rounded-3xl bg-white border-2 border-slate-900/10 shadow-2xl p-4"
            data-testid="mobile-menu"
          >
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="block w-full text-left rounded-2xl px-4 py-3 font-display font-bold text-lg text-slate-800 hover:bg-sky-50"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("booking")}
              data-testid="mobile-book-now"
              className="mt-2 block w-full rounded-2xl bg-sky-400 px-4 py-3 font-display font-bold text-lg text-white text-center"
            >
              Book Now
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
